import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLang } from "@/i18n";

export default function Navbar() {
    const { t, lang, toggle } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [aziendaOpen, setAziendaOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); setAziendaOpen(false); }, [location.pathname]);

    const links = [
        { to: "/", label: t.nav.home },
        { to: "/servizi", label: t.nav.services },
        { to: "/progetti", label: t.nav.projects },
    ];

    const companyLinks = [
        { to: "/squadra", label: t.nav.team },
        { to: "/certificazioni", label: t.nav.certifications },
        { to: "/conto-termico", label: t.nav.contoTermico },
    ];
    const companyActive = companyLinks.some((l) => l.to === location.pathname);

    const solid = scrolled || location.pathname !== "/";

    return (
        <motion.header
            data-testid="site-navbar"
            initial={{ y: -80 }}
            animate={{ y: 0 }}
            transition={{ delay: 2.2, duration: 0.6 }}
            className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                solid
                    ? "bg-cb-dark/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo" className="font-display text-2xl font-bold tracking-tight text-white">
                    Gruppo <span className="text-cb-accent">CB</span> Srl
                </Link>

                <div className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={`text-sm tracking-wide transition-colors relative group ${
                                location.pathname === l.to ? "text-cb-accent" : "text-white/80 hover:text-white"
                            }`}
                        >
                            {l.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-cb-accent transition-all duration-300 group-hover:w-full" />
                        </Link>
                    ))}

                    <div className="relative" onMouseEnter={() => setAziendaOpen(true)} onMouseLeave={() => setAziendaOpen(false)}>
                        <button
                            data-testid="nav-azienda-btn"
                            onClick={() => setAziendaOpen((o) => !o)}
                            className={`flex items-center gap-1 text-sm tracking-wide transition-colors ${
                                companyActive ? "text-cb-accent" : "text-white/80 hover:text-white"
                            }`}
                        >
                            {t.nav.company}
                            <ChevronDown size={15} className={`transition-transform duration-300 ${aziendaOpen ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence>
                            {aziendaOpen && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute top-full left-0 pt-4 w-60"
                                >
                                    <div className="bg-cb-dark border border-white/10 shadow-2xl shadow-black/40 py-2">
                                        {companyLinks.map((l) => (
                                            <Link
                                                key={l.to}
                                                to={l.to}
                                                data-testid={`nav-dropdown-${l.to.replace("/", "")}`}
                                                className={`block px-5 py-3 text-sm transition-colors hover:bg-white/5 hover:text-cb-accent ${
                                                    location.pathname === l.to ? "text-cb-accent" : "text-white/80"
                                                }`}
                                            >
                                                {l.label}
                                            </Link>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <Link
                        to="/contatti"
                        data-testid="nav-link-contatti"
                        className={`text-sm tracking-wide transition-colors relative group ${
                            location.pathname === "/contatti" ? "text-cb-accent" : "text-white/80 hover:text-white"
                        }`}
                    >
                        {t.nav.contact}
                        <span className="absolute -bottom-1 left-0 h-px w-0 bg-cb-accent transition-all duration-300 group-hover:w-full" />
                    </Link>
                </div>

                <div className="flex items-center gap-4">
                    <button
                        data-testid="lang-toggle-btn"
                        onClick={toggle}
                        className="text-xs font-semibold uppercase tracking-widest text-white/70 hover:text-cb-accent transition-colors border border-white/20 px-3 py-1.5"
                    >
                        {lang === "it" ? "EN" : "IT"}
                    </button>
                    <button
                        data-testid="nav-quote-btn"
                        onClick={() => navigate("/preventivo")}
                        className="cb-glow hidden sm:inline-flex bg-cb-accent hover:bg-cb-accentHover text-white text-sm font-medium px-6 py-3 transition-colors"
                    >
                        {t.nav.quote}
                    </button>
                    <button
                        data-testid="mobile-menu-btn"
                        onClick={() => setOpen((o) => !o)}
                        className="lg:hidden text-white"
                        aria-label="Menu"
                    >
                        {open ? <X size={26} /> : <Menu size={26} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="lg:hidden overflow-hidden bg-cb-dark border-t border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {links.map((l) => (
                                <Link key={l.to} to={l.to} className="text-white text-lg" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                                    {l.label}
                                </Link>
                            ))}
                            <div className="pt-3 mt-1 border-t border-white/10">
                                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.nav.company}</span>
                                <div className="mt-3 flex flex-col gap-4">
                                    {companyLinks.map((l) => (
                                        <Link key={l.to} to={l.to} className="text-white/90 text-base" data-testid={`mobile-nav-${l.to.replace("/", "")}`}>
                                            {l.label}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                            <Link to="/contatti" className="text-white text-lg pt-3 border-t border-white/10" data-testid="mobile-nav-contatti">
                                {t.nav.contact}
                            </Link>
                            <button
                                onClick={() => navigate("/preventivo")}
                                className="bg-cb-accent text-white py-3 mt-2"
                                data-testid="mobile-quote-btn"
                            >
                                {t.nav.quote}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
